import React, { FC, useEffect } from 'react';
import './LogOutLayout.css';

import { useAuth } from '@/Domains';
import {
	useAppNavigation,
	Alert,
	PageLoader,
	ActionStatusHelper,
	FeedBackType
} from '@/Shared';

interface LogOutLayoutProps { }

/**
 * =====================================================
 * Component: LogOutLayout
 * =====================================================
 *
 * Description:
 * Handles the user logout process and redirects to the login page.
 * Displays a loader while logout is in progress and shows an error alert if logout fails.
 *
 * Responsibilities:
 * - Trigger logout using the `useAuth` hook
 * - Redirect to login page on successful logout
 * - Show error message if logout fails
 * - Display a loader during the logout process
 *
 * Notes:
 * - Logout is performed immediately upon component mount
 * - Uses `ActionStatusHelper` to track request status
 *
 * Author: Samano CASTRE
 * Date: February 1, 2026
 */
const LogOutLayout: FC<LogOutLayoutProps> = () => {
	
	/** =====================================================
     * Hooks
     * =====================================================
     *
     * - goToLogin: navigation function to redirect user to login page
     * - handleLogOut: triggers logout process
     * - logOutResponse: reactive status of logout request
     */
	const { goToLogin } = useAppNavigation();
	const { handleLogOut, logOutResponse } = useAuth();

	/** =====================================================
     * Effect: Trigger logout on mount
     * =====================================================
     */
	useEffect(() => {
		handleLogOut();
	}, []);

	/** =====================================================
     * Effect: Redirect after successful logout
     * =====================================================
     */
	useEffect(() => {
		if (ActionStatusHelper.isSucceeded(logOutResponse.status)) {
			goToLogin();
			return;
		}
	}, [logOutResponse]);

	if (ActionStatusHelper.isFailed(logOutResponse.status)) {
		return <Alert data={{
			message: logOutResponse.error || "Une erreur s'est produite.",
			type: FeedBackType.ERROR,
		}}
			dismissible={false}
		/>
	}

	return <PageLoader message="Déconnexion en cours. Veuillez patienter..." />;
};

export default LogOutLayout;
